/**
 * Progressive-enhancement layer for the public landing page (Amilli —
 * Public Landing and Preview Specification, P-1). Binds the Horizon Rule
 * sandbox's controls, renders its output, and wires the "Join private beta"
 * dialog. Reuses the same accessible sheet primitive and formatting helpers
 * as the authenticated app (public/js/utils.js) — both are plain,
 * dependency-free ES modules, so importing them here does not pull in the
 * app shell (app.js) or any of its own imports.
 */
import { api } from './api.js';
import { esc, fmt$, signed$, presentSheet, closeSheet } from './utils.js';
import { calculateHorizon, SANDBOX_PERSONA, STATE_COPY } from './sandbox.js';

// ── SANDBOX ─────────────────────────────────────────────────────────────

const hoursSlider  = document.getElementById('lp-hours');
const hoursNumber  = document.getElementById('lp-hours-number');
const hoursValue   = document.getElementById('lp-hours-value');
const wageSlider   = document.getElementById('lp-wage');
const wageNumber   = document.getElementById('lp-wage-number');
const wageValue    = document.getElementById('lp-wage-value');
const commSlider   = document.getElementById('lp-commission');
const commNumber   = document.getElementById('lp-commission-number');
const commValue    = document.getElementById('lp-commission-value');
const outputEl     = document.getElementById('lp-sandbox-output');

/** Keeps a range slider and its paired number input showing the same value. */
function bindPair(slider, number, onChange) {
  const sync = (value, source) => {
    slider.value = value;
    number.value = value;
    onChange(value);
    if (source) source.dispatchEvent(new Event('lp:synced'));
  };
  slider.addEventListener('input', () => sync(slider.value));
  number.addEventListener('input', () => {
    // An empty number field is the deliberate way to reach the zero/cleared
    // state (spec §5.3) — don't coerce it to a number, don't touch the
    // slider (range inputs can't represent "empty"), just recompute.
    if (number.value === '') { onChange(''); return; }
    sync(number.value);
  });
}

function renderOutput(result) {
  if (result.state === 'empty') {
    outputEl.innerHTML = `<div class="lp-sandbox-empty">${esc(STATE_COPY.empty)}</div>`;
    return;
  }

  const stateClass = `lp-state-${result.state}`;
  const stateLabel = result.state === 'deficit'
    ? `${STATE_COPY.deficit}: ${result.deficitHoursNeeded} additional hour${result.deficitHoursNeeded === 1 ? '' : 's'} needed to cover scheduled bills.`
    : STATE_COPY[result.state];

  const milestoneText = result.payPeriodsToMilestone != null
    ? `About ${result.payPeriodsToMilestone} more pay period${result.payPeriodsToMilestone === 1 ? '' : 's'} to reach the ${esc(SANDBOX_PERSONA.milestone.label)} goal at this cushion.`
    : `Reach a positive cushion first to make progress toward the ${esc(SANDBOX_PERSONA.milestone.label)} goal.`;

  const actualPct = result.hoursScheduled > 0
    ? (result.actualHours / result.hoursScheduled * 100).toFixed(1)
    : 0;

  outputEl.innerHTML = `
    <div class="lp-horizon-track" role="img"
         aria-label="${result.actualHours} of ${result.hoursScheduled} scheduled hours logged so far">
      <div class="lp-horizon-fill-projected"></div>
      <div class="lp-horizon-fill-actual" style="width:${actualPct}%"></div>
    </div>
    <div class="lp-horizon-labels">
      <span>${result.actualHours}h logged</span>
      <span>${result.hoursScheduled}h scheduled</span>
    </div>

    <div class="lp-output-grid">
      <div>
        <div class="lp-output-label">Estimated take-home</div>
        <div class="lp-output-value">${fmt$(result.estimatedNet)} <span class="lp-est-tag">(est.)</span></div>
        <div class="lp-output-note" style="color:var(--lp-text-tertiary)">Gross ${fmt$(result.grossPay)}</div>
      </div>
      <div>
        <div class="lp-output-label">Upcoming obligations</div>
        <div class="lp-output-value lp-obligation-value">-${fmt$(result.totalObligations)}</div>
        <div class="lp-output-note" style="color:var(--lp-text-tertiary)">Rent + insurance + phone</div>
      </div>
      <div>
        <div class="lp-output-label">Projected cushion</div>
        <div class="lp-output-value ${stateClass}">${signed$(result.cushion)}</div>
        <div class="lp-output-note ${stateClass}">${esc(stateLabel)}</div>
      </div>
      <div>
        <div class="lp-output-label">Next milestone</div>
        <div class="lp-output-value">${fmt$(SANDBOX_PERSONA.milestone.target)}</div>
        <div class="lp-output-note" style="color:var(--lp-text-tertiary)">${esc(SANDBOX_PERSONA.milestone.label)}</div>
      </div>
    </div>

    <div class="lp-milestone-notch">${esc(milestoneText)}</div>
  `;
}

function recompute() {
  const hoursRaw = hoursNumber.value === '' ? NaN : Number(hoursNumber.value);
  const wage = Number(wageNumber.value) || SANDBOX_PERSONA.hourlyWage;
  const commission = Number(commNumber.value) || 0;

  hoursValue.textContent = hoursNumber.value === '' ? '--' : `${hoursRaw}h`;
  wageValue.textContent = `$${wage.toFixed(2)}`;
  commValue.textContent = `$${commission}`;

  const result = calculateHorizon({
    hoursScheduled: hoursRaw,
    hourlyWage: wage,
    commission,
  });
  renderOutput(result);
}

bindPair(hoursSlider, hoursNumber, recompute);
bindPair(wageSlider, wageNumber, recompute);
bindPair(commSlider, commNumber, recompute);
recompute();

// ── BETA REQUEST DIALOG ────────────────────────────────────────────────

const OCCUPATION_OPTIONS = [
  ['hospitality', 'Hospitality'],
  ['retail', 'Retail'],
  ['contractor', 'Contractor'],
  ['fitness', 'Fitness'],
  ['other', 'Other'],
];
const PAY_CADENCE_OPTIONS = [
  ['weekly', 'Weekly'],
  ['biweekly', 'Biweekly'],
  ['semi_monthly', 'Semi-monthly'],
  ['irregular', 'Irregular'],
];

function openBetaSheet() {
  const content = document.getElementById('sheet-content');
  content.innerHTML = `
    <div class="lp-modal-title">Join the private beta</div>
    <p class="lp-modal-sub">We invite a small cohort every two weeks. No account is created by this form.</p>
    <form id="lp-beta-form">
      <div class="form-group">
        <label class="form-label" for="lp-beta-email">Email</label>
        <input type="email" id="lp-beta-email" required autocomplete="email" placeholder="you@example.com">
      </div>
      <div class="form-group">
        <label class="form-label" for="lp-beta-occupation">What best describes your income?</label>
        <select id="lp-beta-occupation">
          <option value="">Prefer not to say</option>
          ${OCCUPATION_OPTIONS.map(([v, l]) => `<option value="${v}">${esc(l)}</option>`).join('')}
        </select>
      </div>
      <div class="form-group">
        <label class="form-label" for="lp-beta-cadence">How often are you paid?</label>
        <select id="lp-beta-cadence">
          <option value="">Prefer not to say</option>
          ${PAY_CADENCE_OPTIONS.map(([v, l]) => `<option value="${v}">${esc(l)}</option>`).join('')}
        </select>
      </div>
      <div id="lp-beta-error" class="auth-error"></div>
      <button type="submit" class="btn btn-primary w-full" style="justify-content:center;padding:12px">
        <span id="lp-beta-submit-label">Request an invite</span>
      </button>
    </form>
  `;

  const form = content.querySelector('#lp-beta-form');
  const errorEl = content.querySelector('#lp-beta-error');
  const submitLabel = content.querySelector('#lp-beta-submit-label');

  form.addEventListener('submit', async (e) => {
    e.preventDefault();
    const submitBtn = form.querySelector('button[type="submit"]');
    if (submitBtn.disabled) return; // guards a duplicate submit while the request is in flight

    submitBtn.disabled = true;
    submitLabel.textContent = 'Sending…';
    errorEl.classList.remove('show');

    const email = content.querySelector('#lp-beta-email').value.trim();
    const occupationType = content.querySelector('#lp-beta-occupation').value || undefined;
    const payCadence = content.querySelector('#lp-beta-cadence').value || undefined;

    try {
      await api.post('/api/beta-request', { email, occupationType, payCadence });
      content.innerHTML = `
        <div class="lp-modal-success">
          <div class="lp-modal-success-icon">✓</div>
          <div class="lp-modal-title">You're on the list</div>
          <p class="lp-modal-sub">We invite 10 users every two weeks. We'll email ${esc(email)} if a spot opens up.</p>
          <button type="button" class="btn btn-ghost w-full" id="lp-beta-done" style="justify-content:center;padding:12px">Close</button>
        </div>
      `;
      content.querySelector('#lp-beta-done').addEventListener('click', () => closeSheet());
    } catch (err) {
      errorEl.textContent = err?.message || 'Something went wrong. Please try again.';
      errorEl.classList.add('show');
      submitBtn.disabled = false;
      submitLabel.textContent = 'Request an invite';
    }
  });

  presentSheet({ label: 'Join the private beta' });
}

document.getElementById('lp-join-beta-nav').addEventListener('click', openBetaSheet);
document.getElementById('lp-join-beta-hero').addEventListener('click', openBetaSheet);
